import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JourneyContext = createContext(null);

export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return context;
};

export const JourneyProvider = ({ children }) => {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const createJourney = useCallback(async (data = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/journey`, data);
      setJourney(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateJourney = useCallback(async (updates) => {
    if (!journey?.id) {
      // Create journey if not exists
      return createJourney(updates);
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API}/journey/${journey.id}`, updates);
      setJourney(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [journey, createJourney]);

  const trackPageView = useCallback(async (pageName) => {
    if (journey?.id) {
      try {
        await axios.put(`${API}/journey/${journey.id}`, { page_viewed: pageName });
      } catch (err) {
        console.error('Failed to track page view:', err);
      }
    }
  }, [journey]);

  const trackCtaClick = useCallback(async (ctaName) => {
    if (journey?.id) {
      try {
        await axios.put(`${API}/journey/${journey.id}`, { cta_clicked: ctaName });
      } catch (err) {
        console.error('Failed to track CTA click:', err);
      }
    }
  }, [journey]);

  const submitInquiry = useCallback(async (inquiryData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/inquiry`, {
        ...inquiryData,
        journey_id: journey?.id
      });
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [journey]);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
    if (journey?.id) {
      updateJourney({ current_step: step });
    }
  }, [journey, updateJourney]);

  const nextStep = useCallback(() => {
    goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  const resetJourney = useCallback(() => {
    setJourney(null);
    setCurrentStep(1);
    setError(null);
  }, []);

  const value = {
    journey,
    loading,
    error,
    currentStep,
    createJourney,
    updateJourney,
    trackPageView,
    trackCtaClick,
    submitInquiry,
    goToStep,
    nextStep,
    prevStep,
    resetJourney,
    setJourney,
  };

  return (
    <JourneyContext.Provider value={value}>
      {children}
    </JourneyContext.Provider>
  );
};

export default JourneyContext;
